// Parse + classify the opaque `notices` JSON string on a DeviceMediaRequest (a serialized
// List<MediaNotice> the server attaches: gaps, approximate-window, no-footage, partial). Hand-written
// (not generated) on purpose: `notices` rides the wire as an opaque string, so the dashboard parses it
// DEFENSIVELY and tolerates unknown future `kind`s — a new notice kind needs no dashboard change, it
// just renders via `text` + `severity`. Keep this in lock-step with Shared/Models/Api/v1/MediaNotice.cs.

export type MediaNoticeSeverity = "Info" | "Warning" | "Error";

export interface MediaNotice {
    /** Advisory — drives icon/rich rendering; unknown values are tolerated (fall back to text+severity). */
    kind: string;
    severity: MediaNoticeSeverity | string;
    /** Who may see this. Missing/"Everyone" = end-user-visible; anything else is super-admin-only. */
    audience?: string | null;
    /** The user-facing sentence. The contract: always show this when present. */
    text?: string | null;
    rangeStart?: string | null;
    rangeEnd?: string | null;
    gapCount?: number | null;
    totalGapSeconds?: number | null;
}

// Unknown severity sorts as Warning so a future value is still visible (never silently downgraded).
const SEVERITY_RANK: Record<string, number> = { Info: 0, Warning: 1, Error: 2 };
const rankOf = (s: string | undefined) => (s !== undefined && s in SEVERITY_RANK ? SEVERITY_RANK[s] : 1);

/** Defensively parse the opaque notices JSON; any malformed/missing blob yields []. */
export function parseMediaNotices(notices: string | null | undefined): MediaNotice[] {
    if (!notices) return [];
    try {
        const parsed = JSON.parse(notices);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((n): n is MediaNotice => !!n && typeof n === "object");
    } catch {
        return [];
    }
}

/** A notice is end-user-visible unless its audience is explicitly something other than "Everyone".
 *  Unknown/missing audience → visible to everyone; any restricted audience needs super-admin. This
 *  fails safe: a future restricted audience an old client doesn't recognize stays hidden from users. */
export function isNoticeVisible(notice: MediaNotice, isSuperAdmin: boolean): boolean {
    const audience = (notice.audience ?? "Everyone").toString().toLowerCase();
    return audience === "everyone" || isSuperAdmin;
}

/** Parse + drop notices the current user isn't allowed to see (the public/non-admin audience). */
export function visibleNotices(notices: string | null | undefined, isSuperAdmin: boolean): MediaNotice[] {
    return parseMediaNotices(notices).filter((n) => isNoticeVisible(n, isSuperAdmin));
}

/** Radar-specific notice kinds (mirrors MediaNoticeKind in Shared/Models/Api/v1/MediaNotice.cs).
 *  Some views are video-only — radar is noise there even when a request happened to include it. */
const RADAR_NOTICE_KINDS = ["RadarMissing", "RadarMergeFailed"];
export const isRadarNotice = (n: MediaNotice): boolean => RADAR_NOTICE_KINDS.includes(n.kind);

export function maxNoticeSeverity(notices: MediaNotice[]): MediaNoticeSeverity | undefined {
    let best: string | undefined;
    for (const n of notices) {
        if (best === undefined || rankOf(n.severity as string) > rankOf(best)) best = n.severity as string;
    }
    return best as MediaNoticeSeverity | undefined;
}

/** Tailwind text color for a severity (default = Warning amber for unknown). */
export function noticeSeverityClass(severity: string | undefined): string {
    if (severity === "Error") return "text-red-600";
    if (severity === "Info") return "text-gray-500";
    return "text-amber-600";
}
